import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  OrderedPageFilter,
  PagedEmployees,
  defaultFilterValues,
  finishSignup,
  getEmployees,
  getTemplateurl,
  uploadCensusTemplate,
} from 'core'

import { Button, Card, Col, FileUpload, Icon, Notification, Row, Table } from 'common/components'
import { objectToFormData } from 'common/formDataUtilities'
import { useCommandRequestV2, useQueryParamsV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ConfirmFinishSignup from '../../../prospectSignup/step3/ConfirmFinishSignup'
import { employesColumns } from '../../../prospectSignup/step3/EmployeesTableColumns'
import UploadCensusInstructions from '../../../prospectSignup/step3/UploadCensusInstructions'

const { ICHRA_DECISION_TOOL_PATH, PROSPECTS_PATH } = ROUTE_PATHS

const EmployeeCensus = () => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const history = useHistory()
  const { id, companyName } = useQueryParamsV2()

  const [confirmFinishApplication, setConfirmFinishApplication] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [employeesFilter, setEmployeesFilter] = useState<OrderedPageFilter>({
    ...defaultFilterValues,
    orderColumn: 'identifier',
  })

  const {
    data: employees,
    sendQuery: sendGetEmployees,
    isLoading: loadingEmployees,
    reset: resetEmployees,
  } = useQueryRequestV2<PagedEmployees>({ queryFn: () => getEmployees(id ?? '', employeesFilter), initialState: null })

  const { sendQuery: sendTemplateUrl } = useQueryRequestV2<any>()

  const { sendCommand: sendUploadCensusTemplate } = useCommandRequestV2(uploadCensusTemplate)
  const { sendCommand: sendFinishSignup } = useCommandRequestV2(finishSignup)

  const fetchEmployees = async (filter?: OrderedPageFilter) =>
    await sendGetEmployees(() => getEmployees(id ?? '', filter ?? employeesFilter))

  const downloadCensusTemplate = async () => {
    const result = await sendTemplateUrl(() => getTemplateurl())
    window.open(result)
  }

  const onEmployeesTableChange = async (pagination: any, _, sorter: any) => {
    const newFilter = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'identifier',
      orderDescending: sorter.order !== 'ascend',
    }
    setEmployeesFilter(newFilter)
    await fetchEmployees(newFilter)
  }

  const onFileChoosen = async (file: File) => {
    setUploading(true)

    const req = objectToFormData({ prospectId: id ?? '', file })

    const { errors } = await sendUploadCensusTemplate(req)
    errors.length === 0
      ? (Notification.open({
          message: translate('file-uploaded'),
          type: 'success',
        }),
        await fetchEmployees())
      : (resetEmployees(),
        Notification.open({
          message: errors.join(','),
          type: 'error',
        }))

    setUploading(false)
  }

  const finishProspectSignup = async () => {
    setFinishing(true)

    const { errors } = await sendFinishSignup(id ?? '')
    errors.length === 0
      ? history.push(`${ICHRA_DECISION_TOOL_PATH}/${PROSPECTS_PATH}`)
      : Notification.open({
          message: errors.join(','),
          type: 'error',
        })

    setFinishing(false)
  }

  const hasUploadedEmployees = employees && employees.items.length > 0

  const employeesTitle = (
    <div className="table_title_content">
      <h5>
        {translate('prospect-signup.step-3.your-employees')} {`(${employees && employees.totalItemCount})`}
      </h5>
    </div>
  )

  const handleFetch = async () => await fetchEmployees()

  return (
    <>
      <Row>
        <Col md={18}>
          <p>{companyName}</p>
          <span className="color-gray-8">{translate('prospect-signup.step-3.download-template-description')}</span>
          <br />
          <Button customIcon={<Icon type="fa fa-download" />} className="mt-8" onClick={downloadCensusTemplate}>
            {translate('prospect-signup.step-3.download-template')}
          </Button>
        </Col>
        <Col md={18} className="mt-16">
          <span className="color-gray-8">{translate('prospect-signup.step-3.upload-census-description')}</span> <br />
          <UploadCensusInstructions translate={translate} />
          <br />
          <div>
            <FileUpload
              uploadTitle={translate('prospect-signup.step-3.upload-census')}
              onFileChoosed={onFileChoosen}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              uploading={uploading}
            />
          </div>
        </Col>
      </Row>

      {employees && hasUploadedEmployees && (
        <Row>
          <Col className="mt-24">
            <Card title={employeesTitle}>
              <Table
                dataSource={employees.items}
                onChange={onEmployeesTableChange}
                loading={loadingEmployees}
                columns={employesColumns(employeesFilter, handleFetch, translate)}
                pagination={{
                  total: employees.totalItemCount,
                  pageSize: employees.pageSize,
                  current: employees.pageNumber,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '30', '40'],
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div className="mt-32">
        <Col xs={24}>
          <div className="bottom__row">
            <Button
              icon="fa fa-arrow-left"
              size="large"
              htmlType="button"
              type="default"
              onClick={() => {
                history.push(`${ICHRA_DECISION_TOOL_PATH}/${PROSPECTS_PATH}`)
              }}
            >
              {translate('back')}
            </Button>

            {hasUploadedEmployees && (
              <Button
                icon="fa fa-arrow-right"
                iconPosition="right"
                size="large"
                htmlType="submit"
                type="primary"
                className="float-right next__button"
                onClick={() => setConfirmFinishApplication(true)}
              >
                {translate('finish')}
              </Button>
            )}
          </div>
        </Col>
      </div>

      <ConfirmFinishSignup
        visible={confirmFinishApplication}
        headerTitle={translate('prospect-signup.step-3.finish-signup')}
        content={translate('prospect-signup.step-3.finish-signup-body')}
        handleCancel={() => setConfirmFinishApplication(false)}
        handleOk={() => finishProspectSignup()}
        loading={finishing}
      />
    </>
  )
}

export default EmployeeCensus
